import React, { useState, useCallback } from 'react'
import HomeCourtSelector from '../HomeCourtSelector'
import DoublesPartnerSearch from './DoublesPartnerSearch'
import ApiRegistrations from '../../../api/ApiRegistrations'
import { track } from '../../../analytics'
import toastr from 'toastr'
import { withTimeout, getErrorMessage } from '../../../utils/apiHelpers'

function SelectCourtStep({
  selectedCourt,
  onSelectCourt,
  onNext,
  onBack,
  validatedAccessCode,
  onAccessCodeValidated,
  selectedLeague,
  primaryPlayer,
  nominatedPartner,
  onSelectPartner,
}) {
  const isDoublesLeague = selectedLeague?.league_type === 'Doubles'
  const [partnersLoading, setPartnersLoading] = useState(false)
  const [partnerSearchError, setPartnerSearchError] = useState('')
  const canProceed =
    selectedCourt?.id && (!isDoublesLeague || nominatedPartner !== null)
  const [showAccessCode, setShowAccessCode] = useState(false)
  const [accessCode, setAccessCode] = useState('')
  const [validating, setValidating] = useState(false)
  const [codeError, setCodeError] = useState('')

  // Server-side partner search callback passed to DoublesPartnerSearch
  const handlePartnerSearch = useCallback(
    async (query) => {
      if (!selectedLeague?.id || !primaryPlayer?.id) return { partners: [] }
      setPartnersLoading(true)
      setPartnerSearchError('')
      try {
        const response = await ApiRegistrations.getEligiblePartners(
          selectedLeague.id,
          primaryPlayer.id,
          query,
        )
        return response
      } catch (error) {
        setPartnerSearchError(getErrorMessage(error))
        return { partners: [] }
      } finally {
        setPartnersLoading(false)
      }
    },
    [selectedLeague?.id, primaryPlayer?.id],
  )

  const handleApplyCode = async () => {
    if (!accessCode.trim()) {
      setCodeError('Please enter an access code')
      return
    }

    setValidating(true)
    setCodeError('')

    try {
      const result = await withTimeout(
        ApiRegistrations.validateAccessCode(accessCode.trim()),
        30000,
      )

      if (result.valid) {
        toastr.success('Access code applied! You can register for free.')
        onAccessCodeValidated(accessCode.trim())
        setShowAccessCode(false)

        // Track access code usage
        try {
          track('registration_access_code_used', {
            step: 3,
            step_name: 'select_court',
            success: true,
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }
      } else {
        setCodeError(result.message || 'Invalid access code')

        // Track failed access code attempt
        try {
          track('registration_access_code_used', {
            step: 3,
            step_name: 'select_court',
            success: false,
            error: result.message || 'Invalid access code',
          })
        } catch (analyticsErr) {
          console.error('Analytics tracking error:', analyticsErr)
        }
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setCodeError(errorMessage)
    } finally {
      setValidating(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !validating) {
      handleApplyCode()
    }
  }

  return (
    <div className="registration-step">
      <h4 className="step-title">
        {isDoublesLeague
          ? 'Step 3: Select Your Home Court & Partner'
          : 'Step 3: Select Your Home Court'}
      </h4>
      <p className="step-description">
        {isDoublesLeague
          ? 'Choose the court where you prefer to play your home matches, then nominate your doubles partner.'
          : 'Choose the court where you prefer to play your home matches.'}
      </p>

      <HomeCourtSelector value={selectedCourt} onChange={onSelectCourt} />

      <div className="sr-only" aria-live="polite" role="status">
        {selectedCourt
          ? `Selected: ${selectedCourt.name}${
              selectedCourt.city ? ` in ${selectedCourt.city}` : ''
            }`
          : ''}
      </div>

      {/* Doubles Partner Section */}
      {isDoublesLeague && (
        <DoublesPartnerSearch
          onSearch={handlePartnerSearch}
          loading={partnersLoading}
          searchError={partnerSearchError}
          nominatedPartner={nominatedPartner}
          onSelectPartner={onSelectPartner}
        />
      )}

      {/* Access Code Section */}
      <div className="access-code-section mt-4">
        {validatedAccessCode ? (
          <div
            className="alert alert-success d-flex justify-content-between align-items-center"
            role="alert"
          >
            <span>✓ Access code applied - Free registration</span>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                onAccessCodeValidated(null)
                setAccessCode('')
                setCodeError('')
              }}
              aria-label="Remove access code"
            />
          </div>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => setShowAccessCode(!showAccessCode)}
              aria-expanded={showAccessCode}
              aria-controls="access-code-collapse"
            >
              Have an access code? {showAccessCode ? '▼' : '▶'}
            </button>

            {showAccessCode && (
              <div id="access-code-collapse" className="mt-3">
                <div className="mb-2">
                  <label htmlFor="access-code-input" className="form-label">
                    Access Code
                  </label>
                  <input
                    id="access-code-input"
                    type="text"
                    className={`form-control ${codeError ? 'is-invalid' : ''}`}
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Enter your access code"
                    disabled={validating}
                    aria-describedby={
                      codeError ? 'access-code-error' : 'access-code-help'
                    }
                    aria-invalid={!!codeError}
                  />
                  {!codeError && (
                    <div id="access-code-help" className="form-text">
                      Enter a valid access code to register for free
                    </div>
                  )}
                  {codeError && (
                    <div
                      id="access-code-error"
                      className="invalid-feedback"
                      role="alert"
                    >
                      {codeError}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleApplyCode}
                  disabled={validating || !accessCode.trim()}
                  aria-label={
                    validating ? 'Validating access code' : 'Apply access code'
                  }
                >
                  {validating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Validating...
                    </>
                  ) : (
                    'Apply Code'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="step-navigation">
        <button
          className="btn btn-outline-secondary"
          onClick={onBack}
          aria-label="Back to skill level"
        >
          Back
        </button>
        <button
          className="btn btn-primary btn-lg flex-grow-1"
          onClick={onNext}
          disabled={!canProceed}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default SelectCourtStep
